import type { Translate } from "../../../shared/i18n/i18n";
import { Extension } from "@tiptap/react";
import type { Editor, Range } from "@tiptap/react";
import {
  Code2,
  FileUp,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Image as ImageIcon,
  Table as TableIcon,
  TextQuote,
  Type,
  type LucideIcon,
} from "lucide-react";
import { PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import { pickAndInsertFiles } from "./editor-uploads";

export type SlashItem = {
  icon: LucideIcon;
  keywords: string[];
  labelKey: string;
  run: (editor: Editor, range: Range) => void;
  subtextKey: string;
};

// The block palette behind "/" — mirrors the Notion-style set mnemos ships,
// minus embeds that Impacto has no anchors for yet.
export const slashItems: SlashItem[] = [
  {
    keywords: ["p", "paragraph", "text"],
    icon: Type,
    labelKey: "editor.text",
    run: (editor, range) => editor.chain().focus().deleteRange(range).setParagraph().run(),
    subtextKey: "editor.subParagraph",
  },
  {
    keywords: ["h1", "heading", "title"],
    icon: Heading1,
    labelKey: "editor.heading1",
    run: (editor, range) => editor.chain().focus().deleteRange(range).setNode("heading", { level: 1 }).run(),
    subtextKey: "editor.subTopSection",
  },
  {
    keywords: ["h2", "heading", "subheading"],
    icon: Heading2,
    labelKey: "editor.heading2",
    run: (editor, range) => editor.chain().focus().deleteRange(range).setNode("heading", { level: 2 }).run(),
    subtextKey: "editor.subKeySection",
  },
  {
    keywords: ["h3", "heading", "subheading"],
    icon: Heading3,
    labelKey: "editor.heading3",
    run: (editor, range) => editor.chain().focus().deleteRange(range).setNode("heading", { level: 3 }).run(),
    subtextKey: "editor.subSubsection",
  },
  {
    keywords: ["ul", "list", "bullet"],
    icon: List,
    labelKey: "editor.bulletList",
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleBulletList().run(),
    subtextKey: "editor.subUnordered",
  },
  {
    keywords: ["ol", "list", "numbered", "ordered"],
    icon: ListOrdered,
    labelKey: "editor.numberedList",
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
    subtextKey: "editor.subOrdered",
  },
  {
    keywords: ["todo", "task", "checkbox", "checklist"],
    icon: ListTodo,
    labelKey: "editor.todoList",
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleTaskList().run(),
    subtextKey: "editor.subChecklist",
  },
  {
    keywords: ["quote", "blockquote"],
    icon: TextQuote,
    labelKey: "editor.quote",
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
    subtextKey: "editor.subCallout",
  },
  {
    keywords: ["code", "codeblock", "snippet"],
    icon: Code2,
    labelKey: "editor.codeBlock",
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
    subtextKey: "editor.subCode",
  },
  {
    keywords: ["table", "grid"],
    icon: TableIcon,
    labelKey: "editor.table",
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).insertTable({ cols: 3, rows: 3, withHeaderRow: true }).run(),
    subtextKey: "editor.subTable",
  },
  {
    keywords: ["img", "image", "picture", "photo"],
    icon: ImageIcon,
    labelKey: "editor.image",
    run: (editor, range) => {
      editor.chain().focus().deleteRange(range).run();
      pickAndInsertFiles(editor, "image/*");
    },
    subtextKey: "editor.subImage",
  },
  {
    keywords: ["file", "attachment", "upload"],
    icon: FileUp,
    labelKey: "editor.file",
    run: (editor, range) => {
      editor.chain().focus().deleteRange(range).run();
      pickAndInsertFiles(editor, "");
    },
    subtextKey: "editor.subFile",
  },
  {
    keywords: ["hr", "divider", "rule", "separator"],
    icon: Minus,
    labelKey: "editor.divider",
    run: (editor, range) => editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
    subtextKey: "editor.subHorizontal",
  },
];

export function filterSlashItems(query: string, t: Translate): SlashItem[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return slashItems;
  return slashItems.filter(
    (item) => t(item.labelKey).toLowerCase().includes(normalized) || item.keywords.some((keyword) => keyword.startsWith(normalized)),
  );
}

export const slashPluginKey = new PluginKey("slashCommand");

export type SlashPluginState = {
  active: boolean;
  query: string | null;
  range: { from: number; to: number };
};

export function readSlashState(editor: Editor): { from: number; query: string; to: number } | null {
  const state = slashPluginKey.getState(editor.state) as SlashPluginState | undefined;
  if (!state?.active) return null;
  return { from: state.range.from, query: state.query ?? "", to: state.range.to };
}

type SlashKeyHandler = (event: KeyboardEvent) => boolean;

// The suggestion plugin only tracks the "/" match; the menu itself is
// rendered by the React component from the plugin state (via
// useEditorState), and key handling is bridged through editor.storage so it
// always reaches the mounted component — extension closures can outlive a
// React instance (tiptap defers editor destruction across strict-mode
// remounts).
export const SlashCommand = Extension.create({
  name: "slashCommand",

  addStorage() {
    return { onKeyDown: null as SlashKeyHandler | null };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      Suggestion({
        allowSpaces: false,
        char: "/",
        editor,
        items: () => [],
        pluginKey: slashPluginKey,
        render: () => ({
          onKeyDown: ({ event }: { event: KeyboardEvent }) => {
            const handler = (editor.storage as unknown as Record<string, { onKeyDown: SlashKeyHandler | null }>).slashCommand?.onKeyDown ?? null;
            return handler ? handler(event) : false;
          },
        }),
      }),
    ];
  },
});
